import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Box,
  Button,
  Container,
  List,
  ListItemText,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { pdfjs } from "react-pdf";
import { toast } from "react-toastify";

import PDFViewer from "@components/PDFViewer";
import PageTitle from "@components/PageTitle";

import UploadFileModal from "../components/UploadFileModal";
import AuthService from "../services/authService";
import UserService from "../services/userService";
import { extractError } from "../services/utils";
import { COLORS } from "../styles/colors";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export type File = {
  name: string;
  uploadedAt: string;
  createdBy: {
    id: string;
    name: string;
  };
  awsKey?: string;
  id?: string;
};

export const FILE_TYPES = {
  "Home Sleep Test Results": "HST",
  "Lab Results": "LAB&TESTS",
  "CPAP Reports": "DME",
  "General Medical Records": "OTHER",
  "Referral Paperwork": "OTHER",
  Other: "OTHER",
} as const;

export type FileTypeKey = keyof typeof FILE_TYPES;
export type FileType = (typeof FILE_TYPES)[FileTypeKey];

const FilesPage = () => {
  const patientId: string = AuthService.getAuth()?.user?.id;
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState<string>("");

  useEffect(() => {
    getFiles();
  }, [patientId]);

  const getFiles = () => {
    if (!loading) setLoading(true);
    UserService.getPatientFiles(patientId)
      .then((res) => {
        const data: File[] = res.data || [];
        setFiles(data);
      })
      .catch((e) =>
        toast.error(extractError(e, "We are unable to get your files")),
      )
      .finally(() => setLoading(false));
  };

  const handleFileClick = (file: File) => {
    let url = UserService.getFileDownloadUrl(file);
    url = url.replace("?preview=true", "");
    setViewingFile(url);
  };

  const onUpload = async () => {
    getFiles();
  };

  return (
    <Box className="page-wrap">
      <PageTitle title="My Files" />
      <Container>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={() => setIsUploadModalOpen(true)}
            variant="contained"
            color="primary"
          >
            <CloudUploadIcon sx={{ mr: 1, color: "primary.contrastText" }} />
            Upload File
          </Button>
        </Box>

        {files.length ? (
          <List>
            {files.map((file) => (
              <Paper
                key={file.id}
                onClick={() => handleFileClick(file)}
                sx={{
                  borderRadius: "8px",
                  width: "100%",
                  my: 1,
                  padding: "8px 16px",
                  "&:hover": {
                    backgroundColor: "lightgray",
                  },
                  "&:active": {
                    backgroundColor: "gray",
                  },
                  cursor: "pointer",
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      component="span"
                      variant="body1"
                      fontWeight="bold"
                    >
                      {file.name}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        Uploaded by: {file.createdBy.name}
                      </Typography>
                      <br />
                      <Typography component="span" variant="body2">
                        {moment(file.uploadedAt).format("MMM DD, YYYY")}
                      </Typography>
                    </>
                  }
                />
              </Paper>
            ))}
          </List>
        ) : loading ? (
          <List>
            {[...Array(3)].map((_, index) => (
              <Paper
                key={index}
                sx={{
                  borderRadius: "8px",
                  width: "100%",
                  p: 1,
                  my: 1,
                  padding: "8px 16px",
                  "&:hover": { backgroundColor: "lightgray" },
                  "&:active": { backgroundColor: "gray" },
                  cursor: "pointer",
                }}
              >
                <Skeleton variant="text" width="33%" height="50px" />
                <Skeleton variant="text" width="20%" height="30px" />
                <Skeleton variant="text" width="10%" height="30px" />
              </Paper>
            ))}
          </List>
        ) : (
          <Box
            textAlign="center"
            sx={{
              background: "white",
              padding: 2,
              borderRadius: "8px",
              marginTop: 10,
              marginRight: "auto",
              marginLeft: "auto",
              width: "90%",
            }}
          >
            <Typography variant="h6" color={COLORS.DARK_GREY}>
              No files available at this moment
            </Typography>
          </Box>
        )}

        <UploadFileModal
          open={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUpload={onUpload}
        />
        <PDFViewer
          url={viewingFile}
          scale={1.5}
          open={!!viewingFile}
          handleClose={() => setViewingFile("")}
        />
      </Container>
    </Box>
  );
};

export default FilesPage;
